import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Table, Row, Col } from 'react-bootstrap';
import API from '../services/api';
import { dateTime } from './adminShared';

// What each event means, for people (backend services/integrationOutbox.js sends them, Manifold's inbox receives).
const EVENT_LABEL = {
  'sistema.ping': 'Prueba de conexión',
  'cliente.renombrado': 'Cliente renombrado',
  'pedido.entrega_confirmada': 'Entrega confirmada',
  'pedido.devolucion_solicitada': 'Devolución solicitada',
  'pedido.devolucion_cancelada': 'Devolución cancelada',
  'pedido.cancelado': 'Pedido cancelado',
  'pedido.vacios_recibidos': 'Vacíos recibidos',
  'pedido.llenado': 'Tanques llenados',
  'pedido.despachado': 'Despacho',
  'pedido.devolucion_recibida': 'Devolución recibida',
  'pedido.despacho_modificado': 'Despacho modificado',
  'pedido.despacho_eliminado': 'Despacho eliminado',
};
const label = (type) => EVENT_LABEL[type] || type;

// Administración → Sincronización: the link with Manifold Manager (the plant). Events Ventas owes Manifold are kept
// and retried automatically until Manifold takes them; this shows what's still waiting, what Manifold refused (it
// won't be retried on its own: fix the cause, then "Reintentar"), and what Manifold sent that Ventas refused.
const AdminSyncTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => API.get('/admin/integration-status')
    .then(r => setData(r.data))
    .catch(() => setError('No se pudo leer el estado de la sincronización.')), []);
  useEffect(() => { load(); }, [load]);

  const ping = async () => {
    setBusy('ping'); setError(''); setNotice('');
    try {
      const { data: r } = await API.post('/admin/integration-ping');
      if (r.status === 'sent') setNotice('Manifold Manager respondió: la conexión funciona.');
      else setError(r.message || 'Manifold Manager no respondió.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar la prueba.');
    } finally {
      setBusy('');
    }
  };

  const retry = async (eventId) => {
    setBusy(eventId); setError(''); setNotice('');
    try {
      await API.post(`/admin/integration-retry/${eventId}`);
      setNotice('Evento puesto en cola de nuevo; se enviará en unos segundos.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo reintentar.');
    } finally {
      setBusy('');
    }
  };

  if (!data) return <div className="text-muted p-3">{error || 'Cargando...'}</div>;
  const counts = data.outbox.counts || {};
  const failed = data.outbox.problems.filter(p => p.status === 'failed');
  const retrying = data.outbox.problems.filter(p => p.status !== 'failed');

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <Row className="g-3 mb-3">
        {[['Enviados', counts.sent || 0, 'success'], ['En cola', (counts.pending || 0) + (counts.sending || 0), 'warning'], ['Rechazados por Manifold', counts.failed || 0, 'danger'], ['Rechazados aquí', data.inbox.rejected.length, 'danger']].map(([t, n, tone]) => (
          <Col md={3} key={t}>
            <Card className="shadow-sm p-3 h-100">
              <div className="text-muted small">{t}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 600 }} className={n > 0 && tone !== 'success' ? `text-${tone}` : ''}>{n}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="shadow-sm p-3 mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="small text-muted">
            Los cambios que la planta (Manifold Manager) debe conocer se guardan primero y se reintentan solos hasta que Manifold los recibe,
            así que una caída de cualquiera de los dos no pierde nada.
          </div>
          <Button size="sm" variant="outline-primary" disabled={busy === 'ping'} onClick={ping}><i className="bi bi-broadcast" /> Probar conexión</Button>
        </div>
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <h6>Rechazados por Manifold <span className="text-muted small">(no se reintentan solos)</span></h6>
        {failed.length === 0 ? <div className="text-muted small">Ninguno.</div> : (
          <Table size="sm" responsive className="mb-0">
            <thead><tr><th>Evento</th><th>Cuándo</th><th>Motivo</th><th /></tr></thead>
            <tbody>
              {failed.map(p => (
                <tr key={p.eventId}>
                  <td>{label(p.type)}<div className="small text-muted">{p.data?.pedidoNumber || ''}</div></td>
                  <td className="small">{dateTime(p.createdAt)}</td>
                  <td className="small">{p.lastHttpStatus ? `${p.lastHttpStatus} · ` : ''}{p.lastError}</td>
                  <td className="text-end"><Button size="sm" variant="outline-secondary" disabled={busy === p.eventId} onClick={() => retry(p.eventId)}>Reintentar</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <h6>Reintentando <span className="text-muted small">(Manifold no respondió; se vuelve a intentar solo)</span></h6>
        {retrying.length === 0 ? <div className="text-muted small">Nada pendiente.</div> : (
          <Table size="sm" responsive className="mb-0">
            <thead><tr><th>Evento</th><th>Cuándo</th><th>Intentos</th><th>Último error</th><th>Próximo intento</th></tr></thead>
            <tbody>
              {retrying.map(p => (
                <tr key={p.eventId}>
                  <td>{label(p.type)}<div className="small text-muted">{p.data?.pedidoNumber || ''}</div></td>
                  <td className="small">{dateTime(p.createdAt)}</td>
                  <td>{p.attempts}</td>
                  <td className="small">{p.lastError}</td>
                  <td className="small">{dateTime(p.nextAttemptAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="shadow-sm p-3">
        <h6>Enviados por Manifold y rechazados aquí</h6>
        {data.inbox.rejected.length === 0 ? <div className="text-muted small">Ninguno.</div> : (
          <Table size="sm" responsive className="mb-0">
            <thead><tr><th>Evento</th><th>Cuándo</th><th>Motivo</th></tr></thead>
            <tbody>
              {data.inbox.rejected.map(r => (
                <tr key={r.eventId}>
                  <td>{label(r.type)}</td>
                  <td className="small">{dateTime(r.createdAt)}</td>
                  <td className="small"><Badge bg="secondary" className="me-1">{r.httpStatus}</Badge>{r.message}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <div className="small text-muted mt-2">Estos se corrigen en Manifold Manager (su panel de sincronización permite reintentarlos).</div>
      </Card>
    </>
  );
};

export default AdminSyncTab;
